import { SMALL_SCREEN_WIDTH } from '@/config/constant'

// 设计稿基准宽度
const DESIGN_WIDTH = 1728
// 设计稿基准字体大小
const BASE_FONT_SIZE = 16
// 最小字体大小限制
const MIN_FONT_SIZE = 12
// 最大字体大小限制
const MAX_FONT_SIZE = 16

/**
 * 动态设置根元素字体大小
 * 基于设计稿1728px宽度进行比例缩放
 */
export function setDynamicRootFontSize(): void {
    const screenWidth = window.innerWidth
    let fontSize: number

    // 基础计算公式：当前屏幕宽度 / 设计稿宽度 * 基准字体大小
    const baseCalc = (screenWidth / DESIGN_WIDTH) * BASE_FONT_SIZE

    if (screenWidth <= 375) {
        // 小屏手机 (iPhone SE等)
        fontSize = Math.max(baseCalc, MIN_FONT_SIZE)
    } else if (screenWidth <= 414) {
        // 标准手机 (iPhone 6/7/8 Plus等)
        fontSize = Math.max(baseCalc, MIN_FONT_SIZE + 1)
    } else if (screenWidth <= 768) {
        // 大屏手机 / 小平板
        fontSize = baseCalc
    } else if (screenWidth <= 1024) {
        // 平板横屏
        fontSize = baseCalc
    } else if (screenWidth <= 1440) {
        // 小桌面屏幕
        fontSize = baseCalc
    } else if (screenWidth <= DESIGN_WIDTH) {
        // 标准桌面屏幕，接近设计稿尺寸
        fontSize = baseCalc
    } else {
        // 大屏幕，限制最大字体大小
        fontSize = Math.min(baseCalc, MAX_FONT_SIZE)
    }

    // 确保字体大小在合理范围内
    fontSize = Math.max(Math.min(fontSize, MAX_FONT_SIZE), MIN_FONT_SIZE)

    // 设置根元素字体大小
    document.documentElement.style.fontSize = `${fontSize}px`
    document.documentElement.style.setProperty('--font-size-min', `${MIN_FONT_SIZE}px`)

    // 存储当前字体大小供其他地方使用
    if (true) {
        window.__CURRENT_FONT_SIZE__ = fontSize
    }

    // 开发环境下输出调试信息
    if (process.env.NODE_ENV === 'development') {
        console.log(`[响应式字体] 屏幕宽度: ${screenWidth}px, 计算字体大小: ${fontSize.toFixed(2)}px, 缩放比例: ${(fontSize / BASE_FONT_SIZE).toFixed(3)}`)
    }
}

/**
 * 获取当前根元素字体大小
 */
export function getCurrentRootFontSize(): number {
    if (window.__CURRENT_FONT_SIZE__) {
        return window.__CURRENT_FONT_SIZE__
    }
    return BASE_FONT_SIZE
}

/**
 * 将 px 值转换为 rem 值
 * @param px - px 值（数字或字符串）
 * @returns rem 字符串值
 */
export function pxToRem(px: number | string): string {
    const rootValue = 16
    const pxValue = typeof px === 'string' ? parseFloat(px) : px
    const remValue = pxValue / rootValue
    return `${remValue}rem`
}

export const getRatio = () => {
    const currentFontSize = getCurrentRootFontSize()
    const scaleRatio = currentFontSize / BASE_FONT_SIZE

    return scaleRatio
}
/**
 * 调试工具：显示当前缩放信息
 */
export function showScaleInfo(): void {
    const screenWidth = window.innerWidth
    const currentFontSize = getCurrentRootFontSize()
    const scaleRatio = currentFontSize / BASE_FONT_SIZE

    console.group('🎯 响应式字体缩放信息')
    console.log(`设计稿基准宽度: ${DESIGN_WIDTH}px`)
    console.log(`设计稿基准字体: ${BASE_FONT_SIZE}px`)
    console.log(`当前屏幕宽度: ${screenWidth}px`)
    console.log(`当前字体大小: ${currentFontSize.toFixed(2)}px`)
    console.log(`缩放比例: ${scaleRatio.toFixed(3)} (${(scaleRatio * 100).toFixed(1)}%)`)
    console.log(`1rem = ${currentFontSize.toFixed(2)}px`)
    console.groupEnd()
}

/**
 * 初始化响应式字体大小
 * 设置初始字体大小并监听窗口尺寸变化
 */
export function initResponsiveFontSize(): void {
    // 设置初始字体大小
    setDynamicRootFontSize()

    // 监听窗口尺寸变化
    let resizeTimer: number | null = null
    window.addEventListener('resize', () => {
        if (resizeTimer) {
            clearTimeout(resizeTimer)
        }
        // 防抖处理，避免频繁计算
        resizeTimer = window.setTimeout(() => {
            setDynamicRootFontSize()
        }, 100)
    })

    // 监听设备方向变化（移动端）
    window.addEventListener('orientationchange', () => {
        setTimeout(() => {
            setDynamicRootFontSize()
        }, 200)
    })

    // 页面可见性变化时重新计算（用户切换标签页回来时）
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            setTimeout(() => {
                setDynamicRootFontSize()
            }, 100)
        }
    })

    // 开发环境下在控制台添加调试工具
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
        ;(window as any).showScaleInfo = showScaleInfo
        console.log('🎯 调试工具已加载，在控制台输入 showScaleInfo() 查看缩放信息')
    }
}
